"use client";

import { useEffect } from "react";
import {
  getMetadata,
  SITE_URL,
  SITE_NAME,
  DEFAULT_SOCIAL_IMAGE,
  type MetadataPreset,
} from "@/lib/metadata";

type UseMetadataOptions = {
  key?: string;
  preset?: MetadataPreset;
  title?: string;
  description?: string;
  image?: string;
};

export function useMetadata({
  key,
  preset,
  title,
  description,
  image,
}: UseMetadataOptions = {}) {
  const resolved = preset || (key ? getMetadata(key) : undefined);

  useEffect(() => {
    if (!resolved && !title && !description) return;

    const finalTitle = title || resolved?.title || document.title;
    const finalDescription = description || resolved?.description || "";
    const pageImage = image || resolved?.openGraph?.image;
    const imageAlt = resolved?.openGraph?.imageAlt || SITE_NAME;

    document.title = finalTitle;

    const ensureMeta = (attr: string, value: string, propertyName: string) => {
      if (!value) return;
      const selector = `meta[${attr}="${propertyName}"]`;
      let element = document.querySelector(selector) as HTMLMetaElement | null;
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attr, propertyName);
        document.head.appendChild(element);
      }
      element.setAttribute("content", value);
    };

    ensureMeta("property", finalTitle, "og:title");
    ensureMeta("property", finalDescription, "og:description");
    ensureMeta("name", finalDescription, "description");
    ensureMeta("name", finalTitle, "twitter:title");
    ensureMeta("name", finalDescription, "twitter:description");

    const og = resolved?.openGraph;
    if (og?.url) ensureMeta("property", og.url, "og:url");
    if (og?.siteName) ensureMeta("property", og.siteName, "og:site_name");
    if (og?.type) ensureMeta("property", og.type, "og:type");

    if (pageImage) {
      const imageUrl = pageImage.startsWith("http")
        ? pageImage
        : `${SITE_URL}${pageImage}`;
      ensureMeta("property", imageUrl, "og:image");
      ensureMeta("property", imageAlt, "og:image:alt");
      ensureMeta("name", imageUrl, "twitter:image");
    }

    if (resolved?.twitter?.card)
      ensureMeta("name", resolved.twitter.card, "twitter:card");

    // Guard the document title against downstream reconciliation. Next.js_App
    // Router restores the SSR <title> during hydration and on client
    // navigations, which would otherwise overwrite the per-page title set
    // above (it does not touch custom og:/twitter: metas). A MutationObserver
    // re-asserts the intended title whenever anything changes it.
    const titleEl = document.querySelector("title");
    let disconnected = false;
    const observer = new MutationObserver(() => {
      if (disconnected) return;
      if (document.title !== finalTitle) document.title = finalTitle;
    });
    if (titleEl)
      observer.observe(titleEl, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    // One last re-assert after the current task settles.
    const raf = requestAnimationFrame(() => {
      document.title = finalTitle;
    });

    return () => {
      disconnected = true;
      observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [resolved, title, description, image]);
}
